// Schema definitions for each admin content type.
// Each schema declares: resource, title, listFields, formSections (groups of fields).
// Field types: text | textarea | longtext | number | select | tags | image | toggle | list
import { adminAlbums, adminLibrary, adminSymbols, adminRoadhouse, adminObservatory } from "@/api/client";

const statusOpts = [
  { value: "published", label: "Published" },
  { value: "draft", label: "Draft" },
];

export const SCHEMAS = {
  albums: {
    resource: "albums",
    api: adminAlbums,
    title: "Albums",
    singular: "album",
    primary_field: "title",
    list_fields: ["title", "slug", "atmosphere", "year", "status"],
    sections: [
      {
        title: "Identity",
        fields: [
          { name: "title", label: "Title", type: "text", required: true },
          { name: "slug", label: "Slug", type: "text", required: true, hint: "URL fragment, e.g. neon-rodeo" },
          { name: "subtitle", label: "Subtitle / tagline", type: "text",
            hint: "Displayed under the album title on the public page. e.g. \u201cthe desert highway\u201d, \u201cthe fire and the testimony\u201d." },
          { name: "year", label: "Year", type: "text" },
          {
            name: "atmosphere",
            label: "Color palette",
            type: "select",
            hint: "Picks the color wash for the album page and its Archive door. New albums default to \u201cBlue\u201d.",
            options: [
              { value: "blue",       label: "Blue \u2014 desert / neon" },
              { value: "fire",       label: "Fire \u2014 orange / testimony" },
              { value: "archive",    label: "Archive \u2014 violet / memory" },
              { value: "liberation", label: "Liberation \u2014 gold / freedom" },
              { value: "threshold",  label: "Threshold \u2014 twilight / entry" },
              { value: "default",    label: "Default \u2014 neutral" },
            ],
          },
          { name: "status", label: "Status", type: "select", options: statusOpts },
          { name: "sort_order", label: "Sort order", type: "number" },
        ],
      },
      {
        title: "Visual",
        fields: [
          { name: "cover_image_url", label: "Cover image URL or data URI", type: "image" },
          { name: "hero_image_url", label: "Hero image URL (optional)", type: "image" },
        ],
      },
      {
        title: "Words",
        fields: [
          { name: "epigraph", label: "Epigraph", type: "textarea" },
          { name: "lore", label: "Liner notes / lore (markdown)", type: "longtext" },
          { name: "visual_symbolism", label: "Visual symbolism notes", type: "longtext" },
          { name: "themes", label: "Themes", type: "tags", hint: "comma-separated" },
        ],
      },
      {
        title: "Listening",
        fields: [
          {
            name: "streaming_links",
            label: "Streaming links",
            type: "list",
            item_fields: [
              { name: "platform", label: "Platform", type: "text", hint: "spotify / apple_music / bandcamp / youtube" },
              { name: "label", label: "Label override", type: "text" },
              { name: "url", label: "URL", type: "text" },
            ],
          },
          {
            name: "songs",
            label: "Songs",
            type: "list",
            item_fields: [
              { name: "title", label: "Title", type: "text" },
              { name: "order", label: "Order", type: "number" },
              { name: "duration", label: "Duration", type: "text", hint: "e.g. 3:42" },
              { name: "audio_url", label: "Audio URL (mp3 / preview)", type: "text" },
              { name: "lyrics", label: "Lyrics", type: "longtext" },
              { name: "notes", label: "Notes", type: "textarea" },
            ],
          },
          { name: "embed_html", label: "Embed HTML (Spotify/Bandcamp player snippet)", type: "longtext" },
          {
            name: "recovered_fragments",
            label: "Recovered fragments (frequencies, coordinates)",
            type: "list",
            item_fields: [
              { name: "label", label: "Label", type: "text" },
              { name: "value", label: "Value", type: "text" },
            ],
          },
        ],
      },
      {
        title: "Lyric Artifact (booklet reading room)",
        fields: [
          { name: "booklet_label",     label: "Artifact label",     type: "text",
            hint: "Defaults to \u201cOriginal Booklet\u201d if left blank. Try \u201cFirst Edition Booklet\u201d, \u201cMaster Copy\u201d, or \u201cHandwritten Draft\u201d." },
          { name: "booklet_condition", label: "Condition",          type: "text",
            hint: "Defaults to \u201cExcellent\u201d." },
          { name: "booklet_status",    label: "Archive status",     type: "text",
            hint: "Defaults to \u201cAvailable for Consultation\u201d. Set to something like \u201cSealed \u2014 Not Yet Cataloged\u201d to hide the artifact from public view without deleting the pages." },
          { name: "booklet_pages",     label: "Booklet pages (in reading order)", type: "image_list",
            hint: "Add each page as an image, in reading order. When at least one page is present, the Lyric Artifact card and Reading Room appear on the album\u2019s public page." },
        ],
      },
    ],
  },

  library: {
    resource: "library",
    api: adminLibrary,
    title: "Library",
    singular: "library entry",
    primary_field: "title",
    list_fields: ["title", "slug", "type", "status"],
    sections: [
      {
        title: "Entry",
        fields: [
          { name: "title", label: "Title", type: "text", required: true },
          { name: "slug", label: "Slug", type: "text", required: true },
          { name: "subtitle", label: "Subtitle", type: "text" },
          {
            name: "type",
            label: "Type",
            type: "select",
            options: [
              { value: "poem", label: "Poem" },
              { value: "essay", label: "Essay" },
              { value: "manuscript", label: "Manuscript" },
              { value: "publication", label: "Publication" },
            ],
          },
          { name: "date_written", label: "Date / season written", type: "text" },
          { name: "first_appearance", label: "First appearance", type: "text" },
          { name: "status", label: "Status", type: "select", options: statusOpts },
          { name: "sort_order", label: "Sort order", type: "number" },
        ],
      },
      {
        title: "Body",
        fields: [
          { name: "epigraph", label: "Epigraph", type: "textarea" },
          { name: "body", label: "Body (markdown / plain text)", type: "longtext" },
          { name: "tags", label: "Tags", type: "tags" },
          { name: "marginal_notes", label: "Marginal notes", type: "tags", hint: "one per comma" },
        ],
      },
    ],
  },

  symbols: {
    resource: "symbols",
    api: adminSymbols,
    title: "Symbols",
    singular: "symbol",
    primary_field: "name",
    list_fields: ["name", "slug", "status"],
    sections: [
      {
        title: "Symbol",
        fields: [
          { name: "name", label: "Name", type: "text", required: true },
          { name: "slug", label: "Slug", type: "text", required: true },
          { name: "short_definition", label: "Short definition", type: "textarea" },
          { name: "full_description", label: "Full description (markdown)", type: "longtext" },
          { name: "themes", label: "Themes", type: "tags" },
          { name: "related_works", label: "Related work slugs", type: "tags" },
          { name: "image_url", label: "Glyph image (bitmap URL or data URI)", type: "image", hint: "Used when no custom SVG is provided; takes priority over the default line glyph." },
          { name: "image_filter", label: "Glyph image CSS filter (optional)", type: "text", hint: "e.g. 'invert(1)' for dark lines on light background; 'brightness(1.1) contrast(1.1)' for muted images." },
          { name: "glyph_svg", label: "Custom glyph SVG (optional)", type: "longtext" },
          { name: "status", label: "Status", type: "select", options: statusOpts },
          { name: "sort_order", label: "Sort order", type: "number" },
        ],
      },
      {
        title: "Constellation placement",
        fields: [
          { name: "pos_x", label: "Position X (%)", type: "position", hint: "0–100 (left to right). Leave blank to use default layout." },
          { name: "pos_y", label: "Position Y (%)", type: "position", hint: "0–100 (top to bottom). Leave blank to use default layout." },
          { name: "connects_to", label: "Connects to (symbol slugs)", type: "tags", hint: "Comma-separated slugs of other symbols this one links to (e.g. 'crescent-and-star, the-eye'). Connections are bidirectional. Leave blank to use the default groupings. An empty list means this symbol is a singleton with no connections." },
        ],
      },
    ],
  },

  roadhouse: {
    resource: "roadhouse",
    api: adminRoadhouse,
    title: "Roadhouse",
    singular: "roadhouse post",
    primary_field: "title",
    list_fields: ["title", "slug", "type", "event_date", "status"],
    sections: [
      {
        title: "Post",
        fields: [
          { name: "title", label: "Title", type: "text", required: true },
          { name: "slug", label: "Slug", type: "text", required: true },
          {
            name: "type",
            label: "Type",
            type: "select",
            options: [
              { value: "news", label: "News" },
              { value: "event", label: "Event / Show" },
              { value: "release", label: "Release" },
              { value: "press", label: "Press" },
              { value: "field_note", label: "Field Note" },
            ],
          },
          { name: "event_date", label: "Date (ISO)", type: "text", hint: "YYYY-MM-DD" },
          { name: "location", label: "Location", type: "text" },
          { name: "excerpt", label: "Excerpt", type: "textarea" },
          { name: "body", label: "Body", type: "longtext" },
          { name: "image_url", label: "Image URL", type: "image" },
          { name: "link", label: "External link", type: "text" },
          { name: "link_label", label: "Link label", type: "text" },
          { name: "status", label: "Status", type: "select", options: statusOpts },
          { name: "sort_order", label: "Sort order", type: "number" },
        ],
      },
    ],
  },

  observatory: {
    resource: "observatory",
    api: adminObservatory,
    title: "Observatory",
    singular: "artwork",
    primary_field: "title",
    list_fields: ["title", "slug", "medium", "year", "status"],
    sections: [
      {
        title: "Artwork",
        fields: [
          { name: "title", label: "Title", type: "text", required: true },
          { name: "slug", label: "Slug", type: "text", required: true },
          { name: "image_url", label: "Image URL or data URI", type: "image", required: true },
          { name: "medium", label: "Medium", type: "text" },
          { name: "year", label: "Chronology", type: "text" },
          { name: "origin", label: "Origin", type: "text" },
          { name: "description", label: "Description", type: "longtext" },
          { name: "symbolism", label: "Symbolism notes", type: "longtext" },
          { name: "status", label: "Status", type: "select", options: statusOpts },
          { name: "sort_order", label: "Sort order", type: "number" },
        ],
      },
    ],
  },
};

export const DEFAULTS_FOR = (resource) => {
  const s = SCHEMAS[resource];
  if (!s) return {};
  const out = {};
  for (const section of s.sections) {
    for (const f of section.fields) {
      if (f.type === "list") out[f.name] = [];
      else if (f.type === "tags") out[f.name] = [];
      else if (f.type === "image_list") out[f.name] = [];
      else if (f.type === "number") out[f.name] = 0;
      else if (f.type === "position") out[f.name] = null;
      else if (f.type === "select") out[f.name] = f.options?.[0]?.value || "";
      else out[f.name] = "";
    }
  }
  return out;
};
